import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Users, Sparkles, RefreshCw } from "lucide-react";
import {
  PageShell, PageHeader, Pill, Loader, EmptyState, GhostButton,
  NAVY, CHAMPAGNE, GOLD, IVORY, SLATE,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";
import { api } from "@/lib/api";

const KIND_META = {
  diagnostic:   { label: "Diagnostic",  tone: "amber" },
  demo_request: { label: "Démo",        tone: "green" },
};

export default function AdminLeads() {
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const kind = filter === "all" ? "" : `?kind=${filter}`;
      const r = await api.get(`/landing/admin/list${kind}`);
      setData(r.data);
      const rep = await api.get("/landing/cta-report");
      setReport(rep.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const exportCsv = () => {
    const kind = filter === "all" ? "" : `?kind=${filter}`;
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
    // Fetch with auth then trigger download
    fetch(`${base}/api/landing/admin/export.csv${kind}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `altiora-leads-${filter}-${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <PageShell testid="admin-leads-root">
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 pt-8 md:pt-12 pb-20">
        <PageHeader
          eyebrow="Admin · Landing"
          title="Leads reçus depuis la page d'accueil"
          subtitle="Diagnostics onboarding + demandes de démonstration. Export CSV et rapport A/B des CTAs en temps réel."
          testid="admin-leads-header"
        />

        {/* A/B Report */}
        {report && Object.keys(report).length > 0 && (
          <div className="mt-8 rounded-xl p-5 bg-white" style={{ border: `1px solid ${CHAMPAGNE}33` }}
               data-testid="admin-ab-report">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4" style={{ color: GOLD }} strokeWidth={2} />
              <h3 className="font-serif text-lg tracking-tight" style={{ color: NAVY }}>
                Rapport A/B — CTAs du hero
              </h3>
            </div>
            {Object.entries(report).map(([cta, variants]) => (
              <div key={cta} className="mt-3">
                <div className="text-[11px] font-mono uppercase tracking-[0.22em] mb-2" style={{ color: SLATE }}>
                  {cta}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(variants).map(([v, c]) => (
                    <div key={v} className="rounded-md p-3" style={{ background: `${CHAMPAGNE}12`, border: `1px solid ${CHAMPAGNE}22` }}
                         data-testid={`ab-${cta}-${v}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-[12px] font-medium" style={{ color: NAVY }}>Variant {v}</span>
                        <span className="text-[16px] font-serif tabular-nums" style={{ color: GOLD }}>
                          {c.ctr_pct}%
                        </span>
                      </div>
                      <div className="text-[11px] mt-1 font-mono" style={{ color: SLATE }}>
                        {c.click} clics / {c.view} vues
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filters + actions */}
        <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="admin-leads-filters">
          {[
            { k: "all", label: "Tous" },
            { k: "diagnostic", label: "Diagnostics" },
            { k: "demo_request", label: "Démos" },
          ].map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)}
                    data-testid={`filter-${f.k}`}
                    className="text-[12px] px-3 py-1.5 rounded-full transition-all"
                    style={{
                      background: filter === f.k ? NAVY : "transparent",
                      color: filter === f.k ? IVORY : NAVY,
                      border: `1px solid ${filter === f.k ? NAVY : CHAMPAGNE}55`,
                    }}>{f.label}</button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <GhostButton onClick={load} testid="admin-refresh">
              <RefreshCw className="h-3 w-3 mr-1 inline" /> Rafraîchir
            </GhostButton>
            <GhostButton onClick={exportCsv} testid="admin-export-csv">
              <Download className="h-3 w-3 mr-1 inline" /> Export CSV
            </GhostButton>
          </div>
        </div>

        {/* Leads list */}
        <div className="mt-6 rounded-xl bg-white overflow-hidden"
             style={{ border: `1px solid ${CHAMPAGNE}33` }} data-testid="admin-leads-list">
          {loading && <div className="p-8"><Loader label="Chargement…" /></div>}
          {!loading && data?.count === 0 && (
            <EmptyState icon={Users} title="Aucun lead pour ce filtre."
                        description="Les leads capturés depuis /onboarding et /demo apparaissent ici."
                        testid="admin-leads-empty" />
          )}
          {!loading && data?.leads?.map((l) => {
            const meta = KIND_META[l.type] || {};
            return (
              <div key={l.id} className="p-4 border-b last:border-b-0"
                   style={{ borderColor: `${CHAMPAGNE}22` }}
                   data-testid={`lead-${l.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <Pill tone={meta.tone || "neutral"}>{meta.label}</Pill>
                      <span className="font-serif text-[15px]" style={{ color: NAVY }}>
                        {l.name || "(sans nom)"}
                      </span>
                      {l.email && <span className="text-[12px]" style={{ color: SLATE }}>· {l.email}</span>}
                    </div>
                    <div className="text-[12px] mt-1" style={{ color: SLATE }}>
                      {l.profession && <span>{l.profession} · </span>}
                      {l.organization && <span>{l.organization} · </span>}
                      {l.team_size && <span>{l.team_size} · </span>}
                      <span className="font-mono">{l.created_at?.slice(0, 16).replace("T", " ")}</span>
                    </div>
                    {(l.biggest_challenge || l.message) && (
                      <p className="mt-2 text-[13px]" style={{ color: NAVY }}>
                        {l.biggest_challenge || l.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </PageShell>
  );
}
